import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StandardListLayout } from '@/shared/layout/StandardListLayout';
import { Badge, Table, TablePagination, ActionMenu } from '@/components/ui';
import { DataFilterToolbar, type FilterDefinition } from '@/components/ui/DataFilterToolbar';
import { Edit, Printer, Plus, Download } from 'lucide-react';

import { useRequests } from './hooks/useRequests';
import { RequestStatsCards } from './components/RequestStatsCards';
import { RequestMobileList } from './components/RequestMobileList';
import { RequestDetailDrawer } from './components/RequestDetailDrawer';

export function RequestListPage() {
  const { requests, stats, isLoading } = useRequests();
  const navigate = useNavigate();
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('all');

  const getStatusVariant = (status: string): any => {
    switch (status) {
      case 'Chờ duyệt': return 'warning';
      case 'Đang xem xét': return 'info';
      case 'Đã duyệt': return 'success';
      case 'Đã từ chối': return 'danger';
      default: return 'default';
    }
  };

  const getTypeVariant = (type: string): any => {
    return type === 'Thi công' ? 'purple' : 'success';
  };

  const columns = [
    { key: 'checkbox', title: <input type="checkbox" className="rounded border-slate-300" />, width: 40 },
    { key: 'id', title: '#' },
    { key: 'code', title: 'Mã đề xuất' },
    { key: 'title', title: 'Tiêu đề', headerAlign: 'center' as const },
    { key: 'type', title: 'Loại đề xuất', align: 'center' as const },
    { key: 'zone', title: 'Khu vực' },
    { key: 'requester', title: 'Người đề xuất' },
    { key: 'date', title: 'Ngày tạo' },
    { key: 'status', title: 'Trạng thái', align: 'center' as const },
    { key: 'actions', title: 'Thao tác', align: 'center' as const },
  ];

  const filterDefs: FilterDefinition[] = [
    {
      id: 'tab',
      label: 'Danh mục',
      options: [
        { value: 'all', label: 'Tất cả' },
        { value: 'thi-cong', label: 'Thi công' },
        { value: 'bao-duong', label: 'Bảo dưỡng' }
      ]
    },
    {
      id: 'type',
      label: 'Loại đề xuất',
      options: [
        { value: 'all', label: 'Tất cả loại đề xuất' }
      ]
    },
    {
      id: 'status',
      label: 'Trạng thái',
      options: [
        { value: 'all', label: 'Tất cả trạng thái' }
      ]
    },
    {
      id: 'zone',
      label: 'Khu vực',
      options: [
        { value: 'all', label: 'Tất cả khu vực' }
      ]
    }
  ];

  return (
    <StandardListLayout
      title="Đề xuất"
      description="Quản lý và theo dõi các đề xuất trong thi công và bảo dưỡng"
      isLoading={isLoading}
      viewMode="table"
      kpiSection={<RequestStatsCards stats={stats} />}
      toolbarSection={
        <DataFilterToolbar
          search=""
          onSearchChange={() => {}}
          searchPlaceholder="Tìm kiếm theo tiêu đề, mã đề xuất..."
          filterDefs={filterDefs}
          filterValues={{ tab: activeTab, type: 'all', status: 'all', zone: 'all' }}
          onFilterChange={(key, value) => {
            if (key === 'tab') setActiveTab(value || 'all');
          }}
          primaryAction={{
            label: "Tạo đề xuất",
            icon: Plus,
            onClick: () => navigate('/requests/new')
          }}
          secondaryActions={[
            {
              label: "Xuất báo cáo",
              icon: Download,
              onClick: () => {}
            }
          ]}
        />
      }
      tableSection={
        <div className="flex flex-col h-full bg-white rounded-md border border-slate-200">
          <div className="flex-1 overflow-x-auto">
            <Table
              isLoading={isLoading}
              columns={columns}
              data={requests}
              onRowClick={(item) => setSelectedItem(item)}
              renderCell={(item: any, key) => {
                if (key === 'checkbox') return <input type="checkbox" className="rounded border-slate-300" />;
                if (key === 'id') return <span className="sm:text-sm text-xs text-slate-500">{item.id.slice(0,6)}...</span>;
                if (key === 'code') return <span className="p-3 text-blue-600 sm:text-sm text-xs inline-block font-semibold hover:underline cursor-pointer">{item.code || 'N/A'}</span>;
                if (key === 'title') return <span className="sm:text-sm text-xs text-slate-800 font-medium">{item.title}</span>;
                if (key === 'type') return <Badge variant={getTypeVariant(item.type)} className="whitespace-nowrap">{item.type}</Badge>;
                if (key === 'zone') return <span className="sm:text-sm text-xs text-slate-600 font-medium">{item.zone?.zone_name || item.project?.name}</span>;
                if (key === 'requester') {
                  return (
                    <div className="flex items-center gap-2">
                      <img src={item.requester?.avatar_url || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(item.requester?.full_name || 'A')} alt="" className="w-6 h-6 rounded-full border border-slate-100" />
                      <span className="sm:text-sm text-xs text-slate-800 whitespace-nowrap font-medium">{item.requester?.full_name}</span>
                    </div>
                  );
                }
                if (key === 'date') return <span className="sm:text-sm text-xs text-slate-600">{new Date(item.createdAt).toLocaleDateString('vi-VN')}</span>;
                if (key === 'status') return <Badge variant={getStatusVariant(item.status)} className="whitespace-nowrap shadow-sm">{item.status}</Badge>;
                if (key === 'actions') return (
                  <ActionMenu 
                    items={[
                      { label: 'Chỉnh sửa', icon: Edit, onClick: (e: any) => { e.stopPropagation(); } },
                      { label: 'In', icon: Printer, onClick: (e: any) => { e.stopPropagation(); } }
                    ]}
                  />
                );
                return null;
              }}
            />
          </div>
          <div className="p-3 border-t border-slate-100 flex justify-between items-center sm:text-sm text-xs text-slate-500 shrink-0">
            <TablePagination
              currentPage={1}
              totalItems={82}
              pageSize={10}
              onPageChange={() => {}}
              itemLabel="đề xuất"
            />
          </div>
        </div>
      }
      cardSection={
        <RequestMobileList 
          isLoading={isLoading}
          requests={requests}
          onSelectItem={setSelectedItem}
          getStatusVariant={getStatusVariant}
          getTypeVariant={getTypeVariant}
        />
      }
      sidePanel={
        selectedItem ? (
          <RequestDetailDrawer 
            selectedItem={selectedItem}
            onClose={() => setSelectedItem(null)}
            getStatusVariant={getStatusVariant}
            getTypeVariant={getTypeVariant}
          />
        ) : undefined
      }
    />
  );
}

export default RequestListPage;
